import React, { useState, useEffect } from "react";
import smtpApiService from "../services/smtpApiService";
import { toast } from "react-toastify";
import styles from "../style/pages/SmtpConfigPage.module.css";
import { FaEnvelope, FaSave } from "react-icons/fa";

const SmtpConfigPage = () => {
  const [config, setConfig] = useState({
    host: "",
    port: 587,
    username: "",
    password: "",
    fromAddress: "",
    enabled: false,
    useTls: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [testingBtn, setTestingBtn] = useState(false);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const data = await smtpApiService.getSmtpConfig();
        setConfig(data || {
          host: "",
          port: 587,
          username: "",
          password: "",
          enabled: false,
          useTls: true
        });
      } catch (error) {
        toast.error("Failed to fetch SMTP config");
      } finally {
        setLoading(false);
      }
    };
    fetchConfig();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setConfig({
      ...config,
      [name]: type === "checkbox" ? checked : value
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await smtpApiService.saveSmtpConfig(config);
      toast.success("SMTP Configuration saved successfully");
    } catch (error) {
      const errorData = error.response?.data;
      const msg = errorData ? (typeof errorData === 'string' ? errorData : (errorData.message || JSON.stringify(errorData))) : "Unknown error";
      toast.error(`Failed to save SMTP config: ${msg}`);
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    if (!testEmail) {
      toast.error("Please enter an email address to test");
      return;
    }
    setTestingBtn(true);
    try {
      await smtpApiService.testSmtpConfig(testEmail, config);
      toast.success("Test email sent! Please check your inbox.");
    } catch (error) {
      if (error.response?.data) {
        const errorData = error.response.data;
        const msg = typeof errorData === 'string' ? errorData : (errorData.message || JSON.stringify(errorData));
        toast.error(`Test failed: ${msg}`);
      } else {
        toast.error("Failed to send test email");
      }
    } finally {
      setTestingBtn(false);
    }
  };

  if (loading) {
    return <div className={styles.loading}>Loading SMTP Configuration...</div>;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2><FaEnvelope /> SMTP Configuration</h2>
        <p>Configure the mailing server used for sending verification emails.</p>
      </div>

      <form className={styles.form} onSubmit={handleSave}>
        <div className={styles.formGroup}>
          <label>SMTP Host</label>
          <input type="text" name="host" value={config.host} onChange={handleChange} placeholder="smtp.gmail.com" required={config.enabled} />
        </div>

        <div className={styles.formGroup}>
          <label>Port</label>
          <input type="number" name="port" value={config.port} onChange={handleChange} placeholder="587" required={config.enabled} />
        </div>

        <div className={styles.formGroup}>
          <label>Username (Email)</label>
          <input type="text" name="username" value={config.username} onChange={handleChange} placeholder="youremail@example.com" />
        </div>

        <div className={styles.formGroup}>
          <label>Password / App Password</label>
          <input type="password" name="password" value={config.password || ''} onChange={handleChange} placeholder="Enter SMTP password" />
        </div>

        <div className={styles.formGroup}>
          <label><FaEnvelope /> Sender Email (From)</label>
          <input 
            type="email" 
            name="fromAddress" 
            value={config.fromAddress || ''} 
            onChange={handleChange} 
            placeholder="noreply@example.com" 
          />
          <small style={{ color: '#666', display: 'block', marginTop: '5px' }}>
            The email address that will appear in the "From" field. 
            (Required for some relays like Postfix to avoid 554 rejection)
          </small>
        </div>

        <div className={styles.checkboxGroup}>
          <label>
            <input type="checkbox" name="useTls" checked={config.useTls} onChange={handleChange} />
            Use TLS (STARTTLS)
          </label>
        </div>

        <div className={styles.checkboxGroup}>
          <label>
            <input type="checkbox" name="enabled" checked={config.enabled} onChange={handleChange} />
            Enable Email Sending
          </label>
        </div>

        <button type="submit" className={styles.saveBtn} disabled={saving}>
          {saving ? "Saving..." : <><FaSave /> Save Configuration</>}
        </button>
      </form>

      <hr style={{ margin: '30px 0', border: 'none', borderTop: '1px solid #eee' }} />

      <div className={styles.testSection}>
        <h3>Test Configuration</h3>
        <p style={{ fontSize: '14px', color: '#666', marginBottom: '15px' }}>
          Save your configuration above, then enter an email address below to send a test email.
        </p>
        <div style={{ display: 'flex', gap: '10px' }}>
          <input 
            type="email" 
            placeholder="test@example.com" 
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            style={{ flex: 1, padding: '10px', border: '1px solid #ccc', borderRadius: '8px' }}
          />
          <button 
            type="button" 
            className={styles.testBtn} 
            disabled={testingBtn}
            onClick={handleTestEmail}
          >
            {testingBtn ? "Sending..." : "Send Test Email"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SmtpConfigPage;
